import { Badge, Group, Text } from '@mantine/core';
import { IconLock } from '@tabler/icons-react';

/** Props for {@link LockBadge}. */
export interface LockBadgeProps {
  /** Badge text. Default `"Locked"`. */
  label?: string;
  /** Badge size, forwarded to Mantine. Default `sm`. */
  size?: 'xs' | 'sm' | 'md' | 'lg';
}

/**
 * LockBadge — a compact "this is closed" chip.
 *
 * Shell: any.
 *
 * Genuine design-system gap, adopted per issue #258: "a locked / immutable
 * row treatment, shared with closed shows and closed cases rather than
 * invented per surface." Use on a header (a closed show, a closed display
 * case) where the whole object — not one row — is frozen. For a single
 * frozen ROW inside an otherwise-editable list (an expense or settlement
 * outside its rolling edit window), use {@link EditWindowNote} instead, which
 * carries the "since when" context a bare chip cannot.
 */
export function LockBadge({ label = 'Locked', size = 'sm' }: LockBadgeProps) {
  return (
    <Badge
      // `ink`, not `gray` — `gray` is not aliased to the app's ink surface
      // tokens and would render Mantine's stock grey scale instead.
      color="ink"
      variant="light"
      size={size}
      tt="none"
      fw={600}
      radius="var(--radius-chip)"
      leftSection={<IconLock size={11} stroke={2} aria-hidden />}
    >
      {label}
    </Badge>
  );
}

/** Props for {@link EditWindowNote}. */
export type EditWindowNoteProps =
  | {
      /** The row is still inside its rolling edit window. */
      state: 'open';
      /** When the window closes, already formatted (e.g. `"tomorrow, 4:12 PM"`). */
      until: string;
      /** Time remaining, already formatted (e.g. `"23 hours"`). */
      left: string;
    }
  | {
      /** The row's edit window has closed. */
      state: 'locked';
      /** When the window closed, already formatted (e.g. `"Aug 1, 8:40 PM"`). */
      lockedAt: string;
    };

/**
 * EditWindowNote — the sentence line for a single row inside a rolling edit
 * window (expenses, settlements): "Yours to change until … — N left" while
 * open, "🔒 Locked since …" once it closes.
 *
 * Shell: any.
 *
 * Genuine design-system gap, adopted per issue #258, generalising
 * `features/expenses/lib/editability.ts`'s same-day-window concept (there:
 * 24 rolling hours) to any recorder-owned, time-boxed row — expenses and
 * settlements both use exactly this rule
 * (`expenses_recorder_same_day_update` / `settlements_recorder_same_day_update`
 * in `supabase/`). Pure presentational: callers compute `until` / `left` /
 * `lockedAt` from their own clock so this component stays free of a `now()`
 * dependency and is trivially testable.
 */
export function EditWindowNote(props: EditWindowNoteProps) {
  if (props.state === 'open') {
    return (
      <Text fz={12} c="dimmed">
        Yours to change until {props.until} — {props.left} left
      </Text>
    );
  }
  return (
    <Group gap={4} wrap="nowrap">
      <IconLock size={12} stroke={2} color="var(--mantine-color-dimmed)" aria-hidden />
      <Text fz={12} c="dimmed">
        Locked since {props.lockedAt}
      </Text>
    </Group>
  );
}
